<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-card color="transparent" flat>
      <v-toolbar color="transparent" flat>
        <v-toolbar-title class="font-weight-bold"
          >{{$t('geneticsAndBreeding.contemporaryGroupSettings')}}</v-toolbar-title
        >
      </v-toolbar>

      <v-toolbar color="transparent" flat class="mt-4">
        <v-col cols="4" class="pl-0">
          <label class="input-label"
            >{{$t('geneticsAndBreeding.selectTrait')}} <v-icon>mdi-help-circle-outline</v-icon></label
          >
          <v-select
            :items="traits"
            v-model="trait"
            item-text="label"
            item-value="value"
            outlined
            dense
            @change="getContemporaryGroupData('fromSelect')"
          ></v-select>
        </v-col>

        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="$router.push({ name: 'addContemporaryGroup' })"
        >
          {{$t("geneticsAndBreeding.addNewGroup")}}
        </v-btn>
      </v-toolbar>

      <v-card-text class="v-card__body">
        <h3 class="black--text">
          {{ this.traits.find((item) => item.value === this.trait).label }}
          {{$t("geneticsAndBreeding.contemporaryGroups")}}
        </h3>

        <v-card-text class="mt-5 pa-0">
          <v-data-table
            :headers="selectedHeaders"
            :items="items"
            :search="search"
            hide-default-footer
            :items-per-page="3"
            :loading="loading"
            :options.sync="tableOptions"
            :server-items-length="totalItems"
            loading-text="Loading ... Please wait"
          >
            <template v-slot:top>
              <div class="d-flex mt-5">
                <v-col cols="4">
                  <v-text-field
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    height="5px"
                    placeholder="Search"
                    dense
                    v-model="search"
                    class="shrink"
                  ></v-text-field>
                </v-col>
                <v-spacer></v-spacer>
                <div class="py-1 title">
                  {{ from }} - {{ to }} of {{ totalItems }}
                </div>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="tableOptions.page <= 1"
                  @click="pageChange(false)"
                >
                  <v-icon dark> mdi-chevron-left </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="tableOptions.page >= totalPages"
                  @click="pageChange(true)"
                >
                  <v-icon dark> mdi-chevron-right </v-icon>
                </v-btn>

                <v-menu
                  v-model="menu"
                  :close-on-content-click="false"
                  :nudge-width="200"
                  left
                  :min-width="417"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="mx-2"
                      fab
                      small
                      outlined
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon dark> mdi-cog-outline </v-icon>
                    </v-btn>
                  </template>

                  <v-card width="417">
                    <v-list>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>{{
                            $t("customization")
                          }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                      <div class="pl-3 pt-2 font-weight-normal">
                        {{ $t("recordsPerPage") }}
                      </div>
                      <v-list-item>
                        <v-list-item-content>
                          <v-slider
                            v-model="itemsPerPageSlider"
                            :tick-labels="itemsPerPageOptions"
                            :max="3"
                            step="1"
                            ticks="always"
                            tick-size="4"
                          ></v-slider>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-n3"></v-divider>
                    </v-list>
                    <v-card-text>
                      <v-container fluid>
                        <v-row>
                          <v-col
                            class="mt-n9"
                            cols="6"
                            v-for="header in selectedHeaders"
                            :key="header.value"
                          >
                            <v-checkbox
                              v-model="columnsList"
                              :label="header.text"
                              color="primary"
                              :value="header.text"
                              hide-details
                              :disabled="
                                header.text == 'ID' || header.text == 'Tag'
                              "
                            ></v-checkbox>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                    <v-layout justify-center>
                      <v-card-actions class="mb-5">
                        <v-spacer></v-spacer>

                        <v-btn
                          outlined
                          color="primary"
                          @click="menu = false"
                          width="190"
                        >
                          {{ $t("restoreDefault") }}
                        </v-btn>
                        <v-btn
                          color="primary"
                          @click="resetTableStructure"
                          width="190"
                        >
                          {{ $t("apply") }}
                        </v-btn>
                      </v-card-actions>
                    </v-layout>
                  </v-card>
                </v-menu>
              </div>
            </template>
            <template v-if="items.length > 0" v-slot:body="{ items }">
              <tbody v-if="trait === 'birthWeight'">
                <tr
                  v-for="(item, index) in items"
                  :key="index"
                  style="position: relative; width: 0; height: 0"
                >
                  <td>{{ item.groupCode }}</td>
                  <td>{{ item.groupName }}</td>
                  <td>
                    {{ item.birthYearFrom | formatDate }} -
                    {{ item.birthYearTo | formatDate }}
                  </td>
                  <td>{{ item.birthSex }}</td>
                  <td>
                    {{ `${item.birthWeightMin} - ${item.birthWeightMax}` }}
                  </td>
                  <td>
                    {{
                      `${getMonthNames(
                        item.seasonOfBirthFrom
                      )} to ${getMonthNames(item.seasonOfBirthTo)}`
                    }}
                  </td>
                  <td>
                    {{
                      `${item.primaryBreed} (${item.primaryBreedComposition}%)`
                    }}
                  </td>
                  <td>
                    {{
                      `${item.secondaryBreed} (${item.secondaryBreedComposition}%)`
                    }}
                  </td>
                  <td>{{ item.conceptionMethod }}</td>
                  <td>
                    <v-icon
                      color="primary"
                      small
                      class="mr-2 cursor-pointer"
                      @click="editContemporaryGroup(item.id)"
                      >mdi-pencil</v-icon
                    >
                    <v-icon
                      color="primary"
                      small
                      class="cursor-pointer"
                      @click="
                        () => {
                          selectedId = item.id;
                          confirmDialog = true;
                        }
                      "
                    >
                      mdi-delete</v-icon
                    >
                  </td>
                </tr>
              </tbody>

              <tbody v-else-if="trait === 'weaningWeight'">
                <tr
                  v-for="(item, index) in items"
                  :key="index"
                  style="position: relative; width: 0; height: 0"
                >
                  <td>
                    {{ item.birthweightcontemporarygroup?.group_code || "N/A" }}
                  </td>
                  <td>{{ item.groupCode }}</td>
                  <td>{{ item.groupName }}</td>
                  <td>{{ item.dateWeightedFrom | formatDate }}</td>
                  <td>{{ item.weaningSex }}</td>
                  <td>{{ item.primaryBreed }}</td>
                  <td>{{ item.secondaryBreed }}</td>
                  <td>{{ item.conceptionMethod }}</td>
                  <td>
                    <v-icon
                      color="primary"
                      small
                      class="mr-2 cursor-pointer"
                      @click="editContemporaryGroup(item.id)"
                      >mdi-pencil</v-icon
                    >
                    <v-icon
                      color="primary"
                      small
                      class="cursor-pointer"
                      @click="
                        () => {
                          selectedId = item.id;
                          confirmDialog = true;
                        }
                      "
                    >
                      mdi-delete</v-icon
                    >
                  </td>
                </tr>
              </tbody>

              <tbody v-else-if="trait === 'yearlingWeight'">
                <tr
                  v-for="(item, index) in items"
                  :key="index"
                  style="position: relative; width: 0; height: 0"
                >
                  <td>{{ item.groupCode }}</td>
                  <td>
                    {{
                      item.weaningweightcontemporarygroup?.group_code || "N/A"
                    }}
                  </td>
                  <td>{{ item.groupName }}</td>
                  <td>
                    {{
                      `${item.yearlingWeightMin} - ${item.yearlingWeightMax}`
                    }}
                  </td>
                  <td>{{ item.dateWeightedFrom | formatDate }}</td>
                  <td>{{ item.yearlingSex }}</td>
                  <td>
                    <v-icon
                      color="primary"
                      small
                      class="mr-2 cursor-pointer"
                      @click="editContemporaryGroup(item.id)"
                      >mdi-pencil</v-icon
                    >
                    <v-icon
                      color="primary"
                      small
                      class="cursor-pointer"
                      @click="
                        () => {
                          selectedId = item.id;
                          confirmDialog = true;
                        }
                      "
                    >
                      mdi-delete</v-icon
                    >
                  </td>
                </tr>
              </tbody>

              <tbody v-else-if="trait === 'carcassWeight'">
                <tr
                  v-for="(item, index) in items"
                  :key="index"
                  style="position: relative; width: 0; height: 0"
                >
                  <td>
                    {{
                      item.weaningWeightContemporaryGroupId
                        ? item.weaningweightcontemporarygroup?.group_code
                        : item.yearlingweightcontemporarygroup?.group_code ||
                          "N/A"
                    }}
                  </td>
                  <td>{{ item.groupCode }}</td>
                  <td>{{ item.groupName }}</td>
                  <td>{{ item.daysOnFeed }}</td>
                  <td>{{ item.dateOnFeedFrom | formatDate }}</td>
                  <td>{{ item.carcassSex }}</td>
                  <td>{{ item.breedOfDom }}</td>

                  <td>
                    {{ item.harvestDateFrom | formatDate }} -
                    {{ item.harvestDateTo | formatDate }}
                  </td>
                  <td>
                    {{ item.gradingDateFrom | formatDate }} -
                    {{ item.gradingDateTo | formatDate }}
                  </td>
                  <td>
                    {{ item.fatThicknessMin }} - {{ item.fatThicknessMax }}
                  </td>
                  <td>
                    <v-icon
                      color="primary"
                      small
                      class="mr-1 cursor-pointer"
                      @click="editContemporaryGroup(item.id)"
                      >mdi-pencil</v-icon
                    >
                    <v-icon
                      color="primary"
                      small
                      class="cursor-pointer"
                      @click="
                        () => {
                          selectedId = item.id;
                          confirmDialog = true;
                        }
                      "
                    >
                      mdi-delete</v-icon
                    >
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card-text>

      <v-dialog
        v-model="confirmDialog"
        width="500"
        @click:outside="confirmDialog = false"
        :retain-focus="false"
      >
        <v-card class="custom-card" rounded="0">
          <v-toolbar color="secondary" class="white--text" flat>
            <v-toolbar-title class="font-weight-bold">
              {{$t('geneticsAndBreeding.warning')}}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon class="white--text" @click="confirmDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-form lazy-validation autocomplete="off">
            <v-card-text class="black--text">
              <v-row>
                <v-col cols="12">
                  <div class="text-center">
                    <img src="../../../assets/warning.svg" />
                  </div>
                  <div class="py-5 text-center">
                   {{$t('geneticsAndBreeding.deleteCGMsg')}}
                  </div>
                </v-col>
              </v-row>
              <v-divider class="mt-5"></v-divider>
              <v-card-actions class="mt-2" style="padding-right: 0">
                <v-spacer></v-spacer>
                <v-btn
                  outlined
                  class="mr-2"
                  right
                  height="44"
                  width="116"
                  depressed
                  color="primary"
                  @click="confirmDialog = false"
                >
                  {{$t('geneticsAndBreeding.cancel')}}
                </v-btn>
                <v-btn
                  right
                  height="44"
                  width="116"
                  color="primary"
                  depressed
                  @click="deleteContemporaryGroup()"
                >
                  {{$t("geneticsAndBreeding.proceed")}}
                </v-btn>
              </v-card-actions>
            </v-card-text>
          </v-form>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import {
  getBirthWeightContemporaryGroup,
  getWeaningWeightContemporaryGroup,
  getYearlingWeightContemporaryGroup,
  getCarcassWeightContemporaryGroup,
  deleteContemporaryGroup,
} from "@/graphql/EPD.gql";
import loadingMixin from "@/mixins/LoadingMixin";
export default {
  name: "ContemporaryGroupsSetting",
  data() {
    return {
      breadcrumbs: [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("Genetics & Breeding"),
          disabled: false,
          link: true,
          exact: true,
          to: { name: "Genetics" },
        },
        {
          text: this.$t("Contemporary Groups Setting"),
          disabled: true,
        },
      ],
      traits: [
        {
          label: "Birth Weight",
          value: "birthWeight",
        },
        {
          label: "Weaning Weight",
          value: "weaningWeight",
        },
        {
          label: "Yearling Weight",
          value: "yearlingWeight",
        },
        {
          label: "Carcass Weight",
          value: "carcassWeight",
        },
      ],
      trait: "birthWeight",
      requiredFields: [],
      confirmDialog: false,
      from: 0,
      to: 0,
      tableOptions: {},
      loading: false,
      search: "",
      totalPages: 1,
      totalItems: 0,
      birthWeightHeaders: [
        {
          text: this.$t('geneticsAndBreeding.birthCgCode'),
          align: "start",
          sortable: false,
          value: "groupCode",
        },
        { text: this.$t('geneticsAndBreeding.groupName') },
        { text:  this.$t('geneticsAndBreeding.birthYear') },
        { text: this.$t('geneticsAndBreeding.birthSex') },
        { text:  this.$t('geneticsAndBreeding.birthWeight') },
        { text: this.$t('geneticsAndBreeding.seasonofBirth') },
        { text: this.$t('geneticsAndBreeding.primaryBreed') },
        { text:  this.$t('geneticsAndBreeding.secondaryBreed') },
        { text: this.$t('geneticsAndBreeding.conceptionMethod') },
        { text: this.$t('geneticsAndBreeding.action') },
      ],
      weaningWeightHeaders: [
        {
          text: this.$t('geneticsAndBreeding.birthCgCode'),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t('geneticsAndBreeding.weaningCgCode'),
          align: "start",
          sortable: false,
        },
        { text: this.$t('geneticsAndBreeding.groupName') },
        { text: this.$t('geneticsAndBreeding.weaningWeight')  },
        { text: this.$t('geneticsAndBreeding.weaningSex') },

        { text: this.$t('geneticsAndBreeding.primaryBreed') },
        { text:  this.$t('geneticsAndBreeding.secondaryBreed') },
        { text:  this.$t('geneticsAndBreeding.conceptionMethod')},
        { text: this.$t('geneticsAndBreeding.action') },
      ],
      yearlingWeightHeaders: [
        {
          text: this.$t('geneticsAndBreeding.yearlingCgCode'),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t('geneticsAndBreeding.weaningCgCode'),
          align: "start",
          sortable: false,
        },
        { text: this.$t('geneticsAndBreeding.groupName') },
        { text: this.$t('geneticsAndBreeding.yearlingWeight') },
        { text: this.$t('geneticsAndBreeding.dateWeight') },
        { text: this.$t('geneticsAndBreeding.yearlingSex') },
        { text: this.$t('geneticsAndBreeding.action') },
      ],
      carcassWeightHeaders: [
        {
          text: this.$t('geneticsAndBreeding.waningYearlingCgCode'),
          align: "start",
          sortable: false,
        },
        {
          text: this.$t('geneticsAndBreeding.carcassCgCode'),
          align: "start",
          sortable: false,
        },
        { text: this.$t('geneticsAndBreeding.groupName') },
        { text: this.$t('geneticsAndBreeding.daysOnFeed') },
        { text: this.$t('geneticsAndBreeding.dateOnFeed') },
        { text: this.$t('geneticsAndBreeding.carcassSex') },
        { text: this.$t('geneticsAndBreeding.breedOfDom') },
        { text: this.$t('geneticsAndBreeding.harvestDate') },
        { text: this.$t('geneticsAndBreeding.gradingDate') },
        { text: this.$t('geneticsAndBreeding.fatThickness')},
        { text: this.$t('geneticsAndBreeding.action') },
      ],
      items: [],
      months: [
        {
          label: "Jan",
          value: 1,
        },
        {
          label: "Feb",
          value: 2,
        },
        {
          label: "Mar",
          value: 3,
        },
        {
          label: "Apr",
          value: 4,
        },
        {
          label: "May",
          value: 5,
        },
        {
          label: "Jun",
          value: 6,
        },
        {
          label: "Jul",
          value: 7,
        },
        {
          label: "Aug",
          value: 8,
        },
        {
          label: "Sep",
          value: 9,
        },
        {
          label: "Oct",
          value: 10,
        },
        {
          label: "Nov",
          value: 11,
        },
        {
          label: "Dec",
          value: 12,
        },
      ],
      selectedId: "",
      menu: false,
      columnsList: [],
      itemsPerPageSlider: 0,
      itemsPerPageOptions: [10, 25, 50, 100],
      customHeaders: [],
    };
  },
  computed: {
    selectedHeaders() {
      if (this.customHeaders.length > 0) {
        return this.customHeaders;
      }
      if (this.trait === "birthWeight") {
        return this.birthWeightHeaders;
      } else if (this.trait === "weaningWeight") {
        return this.weaningWeightHeaders;
      } else if (this.trait === "yearlingWeight") {
        return this.yearlingWeightHeaders;
      } else if (this.trait === "carcassWeight") {
        return this.carcassWeightHeaders;
      }
      return this.birthWeightHeaders;
    },
  },
  methods: {
    snakeCaseToCamelCase(key) {
      return key
        .toLowerCase()
        .split("_")
        .map((part, i) =>
          i > 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part
        )
        .join("");
    },
    async getContemporaryGroupData(from) {
      this.loading = true;
      if (from === "fromSelect") {
        this.tableOptions.page = 1;
      }
      let rows = null;
      if (this.trait === "birthWeight") {
        const response = await this.$apollo.query({
          query: getBirthWeightContemporaryGroup,
          fetchPolicy: "no-cache",
          variables: {
            contemporaryGroupListInput: {
              page: this.tableOptions.page,
              limit: this.tableOptions.limit,
              searchPhrase: this.search,
            },
          },
        });

        rows = response?.data?.getBirthWeightContemporaryGroup?.rows;
        this.totalItems =
          response?.data?.getBirthWeightContemporaryGroup?.count;
      } else if (this.trait === "weaningWeight") {
        const response = await this.$apollo.query({
          query: getWeaningWeightContemporaryGroup,
          fetchPolicy: "no-cache",
          variables: {
            contemporaryGroupListInput: {
              page: this.tableOptions.page,
              limit: this.tableOptions.limit,
              searchPhrase: this.search,
            },
          },
        });

        rows = response?.data?.getWeaningWeightContemporaryGroup?.rows;
        this.totalItems =
          response?.data?.getWeaningWeightContemporaryGroup?.count;
      } else if (this.trait === "yearlingWeight") {
        const response = await this.$apollo.query({
          query: getYearlingWeightContemporaryGroup,
          fetchPolicy: "no-cache",
          variables: {
            contemporaryGroupListInput: {
              page: this.tableOptions.page,
              limit: this.tableOptions.limit,
              searchPhrase: this.search,
            },
          },
        });
        rows = response?.data?.getYearlingWeightContemporaryGroup?.rows;
        this.totalItems =
          response?.data?.getYearlingWeightContemporaryGroup?.count;
      } else {
        const response = await this.$apollo.query({
          query: getCarcassWeightContemporaryGroup,
          fetchPolicy: "no-cache",
          variables: {
            contemporaryGroupListInput: {
              page: this.tableOptions.page,
              limit: this.tableOptions.limit,
              searchPhrase: this.search,
            },
          },
        });
        rows = response?.data?.getCarcassWeightContemporaryGroup?.rows;
        this.totalItems =
          response?.data?.getCarcassWeightContemporaryGroup?.count;
      }

      if (rows) {
        this.items = rows.map((item) => {
          for (const [key, value] of Object.entries(item)) {
            delete item[key];
            const sanitizedKey = this.snakeCaseToCamelCase(key);
            item[sanitizedKey] = value;
          }
          return item;
        });

        this.totalPages = Math.ceil(
          this.totalItems / this.tableOptions.itemsPerPage
        );
        this.from =
          (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage + 1;
        this.to =
          this.tableOptions.page * this.tableOptions.itemsPerPage -
          (this.tableOptions.itemsPerPage - rows?.length);
      } else {
        this.$notify({
          title: `Success!!!`,
          text: "Error fetching contemporary group data",
          type: "error",
        });
      }
      this.loading = false;
    },
    pageChange(t) {
      if (t) {
        this.tableOptions.page = this.tableOptions.page + 1;
        return;
      } else {
        this.tableOptions.page = this.tableOptions.page - 1;
        return;
      }
    },
    getMonthNames(num) {
      const month = this.months.find((item) => item.value == num);
      return month.label;
    },
    async deleteContemporaryGroup() {
      this.startLoading();
      this.$apollo
        .mutate({
          // Query
          mutation: deleteContemporaryGroup,
          // Parameters
          variables: {
            deleteContemporaryGroupInput: {
              traitType: this.trait,
              id: this.selectedId,
            },
          },
        })
        .then(async () => {
          this.stopLoading();
          this.$router.push({ name: "contemporaryGroups" });
          this.$notify({
            title: this.$t("geneticsAndBreeding.deleteSuccess"),
            text: this.$t("geneticsAndBreeding.deleteSuccessMsg"),
            type: "success",
          });
          this.confirmDialog = false;
          this.getContemporaryGroupData();
          this.stopLoading();
        })
        .catch(async (error) => {
          console.log(error);
          this.$notify({
            title: this.$t("geneticsAndBreeding.deleteFailed"),
            text: this.$t("geneticsAndBreeding.deleteFailedMsg"),
            type: "error",
          });
          this.stopLoading();
        });
    },
    editContemporaryGroup(id) {
      this.$router.push({
        name: "editContemporaryGroup",
        params: { trait: this.trait, id },
      });
    },
    resetTableStructure() {
      const currentHeaders = this.selectedHeaders;
      const customHeaders = currentHeaders.filter((item) =>
        this.columnsList.includes(item.text)
      );
      this.customHeaders = customHeaders;
    },
  },
  watch: {
    tableOptions: {
      handler() {
        this.getContemporaryGroupData();
      },
      deep: true,
      immediate: false,
    },
    search: {
      handler() {
        this.getContemporaryGroupData();
      },
      deep: true,
    },
  },
  created() {},
  mixins: [loadingMixin],
};
</script>
<style scoped>
.v-card__body {
  min-height: 450px;
  background-color: #ffffff;
  padding: 24px;
}

.grey-hr {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-bottom: none;
}

.input-label {
  color: #1e1e1e;
  font-size: 14px;
  margin-bottom: 8px;
  display: block;
}

.input-label i {
  font-size: 20px;
  margin-left: 8px;
}

.bottom-banner {
  margin-top: 60px;
  background-color: #fff7ec;
  border: 1px solid #ffb443;
  border-radius: 5px;
  padding: 18px 26px;
}

.bottom-banner strong {
  font-weight: 600;
  color: #1e1e1e;
}
.title {
  color: #787878;
  font-size: 14px !important;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
